/** Friendly schedule model that maps to a 5-field Mauritius Time (MUT) cron expression. */

export type ScheduleFrequency = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

export interface FriendlySchedule {
  frequency: ScheduleFrequency;
  /** 0–23 (MUT) */
  hour: number;
  /** 0–59 */
  minute: number;
  /** 0=Sunday … 6=Saturday — used when frequency is weekly */
  dayOfWeek: number;
  /** 1–31 — used when frequency is monthly */
  dayOfMonth: number;
  /** Raw cron when frequency is custom (or unrecognized) */
  cron: string;
}

export const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
] as const;

export const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'custom', label: 'Custom (advanced)' },
];

export const DEFAULT_FRIENDLY_SCHEDULE: FriendlySchedule = {
  frequency: 'daily',
  hour: 9,
  minute: 0,
  dayOfWeek: 1,
  dayOfMonth: 1,
  cron: '0 9 * * *',
};

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatUtcTime(hour: number, minute: number): string {
  return `${pad2(clamp(hour, 0, 23))}:${pad2(clamp(minute, 0, 59))}`;
}

/** Build a 5-field cron from a friendly schedule (Mauritius Time). */
export function scheduleToCron(schedule: FriendlySchedule): string {
  if (schedule.frequency === 'custom') {
    return schedule.cron.trim();
  }
  const minute = clamp(schedule.minute, 0, 59);
  const hour = clamp(schedule.hour, 0, 23);
  switch (schedule.frequency) {
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`;
    case 'weekly':
      return `${minute} ${hour} * * ${clamp(schedule.dayOfWeek, 0, 6)}`;
    case 'monthly':
      return `${minute} ${hour} ${clamp(schedule.dayOfMonth, 1, 31)} * *`;
    default:
      return `${minute} ${hour} * * *`;
  }
}

/**
 * Parse a cron into a friendly schedule when it matches known patterns.
 * Unrecognized expressions become `custom`.
 */
export function cronToSchedule(cron: string | undefined | null): FriendlySchedule {
  const raw = (cron ?? '').trim();
  if (!raw) {
    return { ...DEFAULT_FRIENDLY_SCHEDULE };
  }

  const parts = raw.split(/\s+/);
  if (parts.length !== 5) {
    return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'custom', cron: raw };
  }

  const [minStr, hourStr, domStr, monthStr, dowStr] = parts;
  const minute = Number(minStr);
  const hour = Number(hourStr);

  if (
    !/^\d+$/.test(minStr) ||
    !/^\d+$/.test(hourStr) ||
    minute < 0 ||
    minute > 59 ||
    hour < 0 ||
    hour > 23
  ) {
    return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'custom', cron: raw };
  }

  // Only simple month=* patterns are supported in the friendly UI
  if (monthStr !== '*') {
    return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'custom', cron: raw, hour, minute };
  }

  if (domStr === '*' && dowStr === '*') {
    return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'daily', hour, minute, cron: raw };
  }

  if (domStr === '*' && dowStr === '1-5') {
    return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'weekdays', hour, minute, cron: raw };
  }

  if (domStr === '*' && /^\d+$/.test(dowStr)) {
    const dayOfWeek = Number(dowStr);
    if (dayOfWeek >= 0 && dayOfWeek <= 6) {
      return {
        ...DEFAULT_FRIENDLY_SCHEDULE,
        frequency: 'weekly',
        hour,
        minute,
        dayOfWeek,
        cron: raw,
      };
    }
  }

  if (dowStr === '*' && /^\d+$/.test(domStr)) {
    const dayOfMonth = Number(domStr);
    if (dayOfMonth >= 1 && dayOfMonth <= 31) {
      return {
        ...DEFAULT_FRIENDLY_SCHEDULE,
        frequency: 'monthly',
        hour,
        minute,
        dayOfMonth,
        cron: raw,
      };
    }
  }

  return { ...DEFAULT_FRIENDLY_SCHEDULE, frequency: 'custom', cron: raw, hour, minute };
}

/** Human-readable summary for the properties panel. */
export function describeSchedule(schedule: FriendlySchedule): string {
  if (schedule.frequency === 'custom') {
    const cron = schedule.cron.trim();
    return cron ? `Custom schedule: ${cron} (MUT)` : 'Custom schedule (set a cron expression)';
  }

  const time = formatUtcTime(schedule.hour, schedule.minute);
  switch (schedule.frequency) {
    case 'daily':
      return `Every day at ${time} MUT`;
    case 'weekdays':
      return `Every weekday (Mon–Fri) at ${time} MUT`;
    case 'weekly': {
      const day = WEEKDAY_OPTIONS.find((d) => d.value === schedule.dayOfWeek)?.label ?? 'Monday';
      return `Every ${day} at ${time} MUT`;
    }
    case 'monthly':
      return `On day ${clamp(schedule.dayOfMonth, 1, 31)} of each month at ${time} MUT`;
    default:
      return `At ${time} MUT`;
  }
}

/** Parse an `HH:MM` time string into hour/minute; returns null if invalid. */
export function parseTimeInput(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}
